import api from './api';
import type { Product, FindByNameResponse } from '../types';

export const productsService = {
  async getById(id: string): Promise<Product> {
    const response = await api.get<Product>(`/products/${id}`);
    return response.data;
  },

  async getByBarcode(barcode: string): Promise<Product | null> {
    try {
      const response = await api.get<Product>(`/products/barcode/${barcode}`);
      return response.data;
    } catch (error) {
      return null;
    }
  },

  async search(query: string): Promise<Product[]> {
    const response = await api.get<Product[]>('/products/search', {
      params: { query },
    });
    return response.data;
  },

  async getAll(page = 1, pageSize = 50): Promise<{ items: Product[]; totalCount: number; page: number; pageSize: number }> {
    const response = await api.get<{ items: Product[]; totalCount: number; page: number; pageSize: number }>('/products', {
      params: { page, pageSize },
    });
    return response.data;
  },

  async getBrandSuggestions(query: string): Promise<string[]> {
    const response = await api.get<string[]>('/products/brands', { params: { q: query } });
    return response.data;
  },

  async getTopCategories(limit = 8): Promise<{ category: string; productCount: number; emoji?: string | null }[]> {
    const response = await api.get<{ category: string; productCount: number; emoji?: string | null }[]>('/products/categories/top', {
      params: { limit },
    });
    return response.data;
  },

  async getCategories(): Promise<string[]> {
    const response = await api.get<string[]>('/products/categories');
    return response.data;
  },

  async getCategoriesGrouped(): Promise<Record<string, string[]>> {
    const response = await api.get<Record<string, string[]>>('/products/categories/grouped');
    return response.data;
  },

  async getTrending(limit = 5): Promise<Product[]> {
    const response = await api.get<Product[]>('/analytics/trending/products', { params: { limit, period: 0 } });
    return response.data;
  },

  async findByName(query: string): Promise<FindByNameResponse> {
    const response = await api.post<FindByNameResponse>('/products/find-by-name', { query });
    return response.data;
  },

  async findSimilarByName(name: string, brand?: string, limit = 5): Promise<Product[]> {
    const response = await api.get<Product[]>('/products/find-similar', { params: { name, brand, limit } });
    return response.data;
  },

  async getSimilar(productId: string, limit = 5): Promise<Product[]> {
    const response = await api.get<Product[]>(`/products/${productId}/similar`, { params: { limit } });
    return response.data;
  },

  async getReviewers(productId: string, limit = 3): Promise<{ id: string; username: string; profileImageUrl?: string; reviewCount: number; followerCount: number }[]> {
    const response = await api.get(`/products/${productId}/reviewers`, { params: { limit } });
    return response.data;
  },

  async getByCategory(
    category: string,
    page = 1,
    pageSize = 20,
    sort = 'top_rated',
  ): Promise<{ products: Product[]; totalCount: number; page: number; pageSize: number; totalPages: number }> {
    const response = await api.get(`/products/category/${encodeURIComponent(category)}`, {
      params: { page, pageSize, sort },
    });
    return response.data;
  },

  async getAmazonLink(id: string): Promise<{ affiliateUrl: string; imageUrl?: string }> {
    const response = await api.get<{ affiliateUrl: string; imageUrl?: string }>(`/products/${id}/amazon`);
    return response.data;
  },
};
